import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import FileIcon from '../../utils/FileIcon';
import { InlineEditor } from 'ckeditor5-custom-build/build/ckeditor';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { getUser } from '../../utils/tokens';
import { loadImages } from '../../utils/FileHelper';
import ResourceCard from './ResourceCard';

const getLocalResourcesUrl = clientId => `resource/v1/resources/clients/${clientId}?enabled=true`;
const getResourcesUrl = categoryId => `/resource/v1/resource-categories/${categoryId}/resources`;
const getImgUrl = id => `/resource/v1/resources/${id}/file/${id}`;

const ResourceCardsPage = ({ resources }) => {
	const [categoryDescription, setCategoryDescription] = useState(null);
	let navigate = useNavigate();

	useEffect(() => {
		if (resources?.length > 0) {
			if (resources[0].resourceCategory.description && resources[0].resourceCategory.description.length > 1) {
				setCategoryDescription(resources[0].resourceCategory.description);
			}
		}
	}, []);

	function onClickResource(resource) {
		if (resource.type === 'INLINE') {
			navigate('view/' + resource.id);
		} else if (resource.type === 'LINK') {
			if (resource.resourceLinks[0]) {
				window.open(resource.resourceLinks[0].link, '_blank');
			}
		} else if (resource.type === 'ATTACHMENT') {
			if (resource.resourceAttachments.length > 0) {
				navigate('preview/' + resource.id);
			}
		}
	}

	return (
		<div className="relative px-4 pt-8 pb-8 sm:px-6 lg:px-8 lg:pt-8 lg:pb-8">
			<div className="relative mx-auto max-w-7xl">
				{categoryDescription && (
					<div className="sm:col-span-6 bg-white p-2 rounded-lg border-2 border-slate-300">
						<CKEditor
							editor={InlineEditor}
							data={categoryDescription}
							onReady={editor => {
								const toolbarElement = editor.ui.view.toolbar.element;
								toolbarElement.style.display = 'none';
								const myFeatureLockId = Symbol('my-feature');
								editor.enableReadOnlyMode(myFeatureLockId);
							}}
						/>
					</div>
				)}

				{resources?.length > 0 && !categoryDescription && (
					<div className="text-left pt-8">
						<h2 className="text-3xl font-bold tracking-tight text-gray-900">Resources</h2>
					</div>
				)}

				<div className="mx-auto mt-12 grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-4 md:grid-cols-2 2xl:grid-cols-4">
					{resources?.length > 0 &&
						resources.map(res => <ResourceCard key={res.id} resource={res} onClick={onClickResource} />)}
				</div>
			</div>
		</div>
	);
};

/**
 *
 * @param resource
 * @returns {JSX.Element}
 * @constructor
 */
export const ResourceImage = ({ resource }) => {
	let img;
	if (resource.imageId) {
		img = <img src={resource.imageUrl} className="rounded-t-lg w-full object-cover" alt={resource.name} />;
	} else if (resource.resourceAttachments.length > 0) {
		img = (
			<div className="flex items-center">
				<FileIcon
					className="basis-2/12 my-0.5 h-48"
					type="FILE"
					contentType={resource.resourceAttachments[0].contentType}
					ext={resource.resourceAttachments[0].ext}
					size={100}
				/>
			</div>
		);
	} else if (resource.type === 'LINK') {
		img = (
			<div className="flex items-center">
				<FileIcon className="basis-2/12 my-0.5" size={100} />
			</div>
		);
	} else {
		img = (
			<div className="flex items-center">
				<FileIcon className="basis-2/12 my-0.5" type="FILE" ext="txt" size={100} />
			</div>
		);
	}

	return img;
};

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
const ResourceCardsView = ({ local, updateResources }) => {
	const [resources, setResources] = useState(null);
	const [resourcesLoaded, setResourcesLoaded] = useState(false);
	const [imagesLoaded, setImagesLoaded] = useState(null);
	const params = useParams();
	const currentUser = getUser();

	useEffect(() => {
		const getLocalResources = async () => {
			return await axios
				.get(getLocalResourcesUrl(currentUser.clientIdNum))
				.then(response => {
					setResources(response.data);
				})
				.catch(error => {
					console.log(error);
				});
		};

		const fetchResources = async () => {
			const url =
				params.type === 'global'
					? getResourcesUrl(params.categoryId) + '?global=true'
					: getResourcesUrl(params.categoryId);
			const response = await axios({
				method: 'GET',
				url: url,
			});
			setResources(response.data);
			setResourcesLoaded(true);
		};

		setImagesLoaded(v => false);
		if (params.categoryId) {
			fetchResources().catch(console.error);
		} else if (local) {
			getLocalResources().catch(console.error);
		}
	}, [params]);

	useEffect(() => {
		loadImages({ url: getImgUrl, objects: resources, setImagesLoaded: setImagesLoaded });
	}, [resources]);

	if (resources?.length && imagesLoaded) {
		updateResources(true);

		return <ResourceCardsPage resources={resources} />;
	} else {
		if (resources?.length === 0 && resourcesLoaded) {
			updateResources(false);
		}
	}
};

export default ResourceCardsView;
